/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/comps/ConfigurationProvider.tsx
 * @Description: 运营配置 Provider
 *
 * 在客户端获取运营配置数据，并通过 Context 提供给子组件
 * 用于功能开关、业务规则等配置
 */

import { ReactNode } from 'react';
import { ConfigurationContext } from 'libs/contexts';
import { useConfigurationQuery } from 'hooks/useConfigurationQuery';

export interface ConfigurationProviderProps {
  children: ReactNode;
  /**
   * 初始数据（可选）
   * 用于服务端渲染场景，如果提供则使用初始数据，避免客户端重新请求
   */
  initialData?: any[];
}

/**
 * 运营配置 Provider 组件
 *
 * 功能：
 * 1. 使用 React Query 在客户端获取运营配置
 * 2. 通过 ConfigurationContext 提供数据给子组件
 * 3. 自动处理 loading 和 error 状态
 * 4. 支持初始数据，避免重复请求
 *
 * @example
 * ```tsx
 * // 在 _app.tsx 中使用
 * <ConfigurationProvider>
 *   <YourApp />
 * </ConfigurationProvider>
 *
 * // 在子组件中使用
 * function SomeComponent() {
 *   const configuration = useContext(ConfigurationContext);
 *   const enableFeature = configuration?.find(item => item.key === 'enableNewFeature')?.value;
 *   return <div>{enableFeature ? '新功能已启用' : '新功能未启用'}</div>;
 * }
 * ```
 */
export default function ConfigurationProvider({
  children,
  initialData,
}: ConfigurationProviderProps) {
  // 使用 React Query 获取运营配置
  const {
    data: configuration,
    isLoading,
    isError,
    error,
  } = useConfigurationQuery({
    initialData,
  });

  // 错误处理：如果加载失败，使用空数组避免组件崩溃
  if (isError) {
    console.error('加载运营配置失败:', error);
  }

  // 提供数据给子组件
  // 注意：
  // 1. loading 期间提供空数组，避免子组件因为 undefined 崩溃
  // 2. error 期间也提供空数组，让应用继续运行
  // 3. 成功后提供实际数据
  const configData = isLoading || isError ? [] : configuration || [];

  return (
    <ConfigurationContext.Provider value={configData}>{children}</ConfigurationContext.Provider>
  );
}
